import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { useMemo } from 'react';
import { observer } from 'mobx-react';
import { useLocalization } from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';

const LEGAL_LINKS = formatMessage => [
  {
    to: '/legal/terms',
    label: formatMessage({ id: 'TERMS', defaultMessage: 'Terms' }),
  },
  {
    to: '/legal/privacy',
    label: formatMessage({ id: 'PRIVACY', defaultMessage: 'Privacy' }),
  },
  {
    to: '/legal/acceptable-use',
    label: formatMessage({
      id: 'ACCEPTABLE_USE',
      defaultMessage: 'Acceptable Use',
    }),
  },
  {
    to: '/legal/security',
    label: formatMessage({
      id: 'SECURITY_DISCLOSURE',
      defaultMessage: 'Security Disclosure',
    }),
  },
  {
    to: '/public/help',
    label: formatMessage({
      id: 'HELP_SUPPORT',
      defaultMessage: 'Help & Support',
    }),
  },
];

function Footer({ maxWidth = 'xl', ...props }) {
  const { formatMessage } = useLocalization();
  const { isAuthenticated = false } = useAuth() || {};
  const { sx, ...otherProps } = props;

  const links = useMemo(() => {
    return LEGAL_LINKS(formatMessage).map((link, index) =>
      index === 4 && isAuthenticated
        ? {
            to: '/help',
            label: formatMessage({
              id: 'HELP_SUPPORT',
              defaultMessage: 'Help & Support',
            }),
          }
        : link,
    );
  }, [isAuthenticated, formatMessage]);

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        py: 2,
        px: 2,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'neutral.subtle',
        ...sx,
      }}
      {...otherProps}>
      <Container maxWidth={maxWidth}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {links.map(({ to, label }, index) => (
              <Box key={to}>
                <MuiLink
                  component={Link}
                  to={to}
                  variant="body2"
                  color="text.secondary"
                  underline="hover">
                  {label}
                </MuiLink>

                {index < links.length - 1 && (
                  <Typography
                    sx={{ ml: 1 }}
                    component="span"
                    variant="body2"
                    color="text.secondary">
                    |
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            &copy; {new Date().getFullYear()} Service Xcelerator, Inc.{' '}
            <FormattedMessage
              id="ALL_RIGHTS_RESERVED"
              defaultMessage="All rights reserved"
            />
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default observer(Footer);
